import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

/**
 * Current CMS page state. If `useI18n()` turns out not to resolve in a store's setup under SSR, the
 * fallback is `useNuxtApp().$i18n` or passing the locale as an action argument instead.
 */
export const usePageStore = defineStore('page', () => {
  const cacheStore = useCacheStore()
  const cmsFetch = useCmsFetch()
  const { locale } = useI18n()

  const page = ref<any>(undefined)

  const languageSwitches = computed(() => {
    const current = page.value
    return (current?.alternativeLanguages || []).map(({ language, url }: { language: string; url: string }) => ({
      language,
      url,
      isCurrent: url === current.url,
    }))
  })

  function setPage(newPage: any) {
    page.value = newPage
  }

  async function fetchUri({ uri, push = false, options = {} }: { uri: string; push?: boolean; options?: object }) {
    const key = `page;v2;locale:${locale.value};uri:${uri}`
    const ttl = 300

    const fetcher = async () => {
      // Craft stores entry uris without a leading slash, Retour stores its source urls with one, so an
      // uri that only exists as a redirect is found by the second candidate.
      const requestedUri = String(uri || '').replace(/^\/+/, '')
      let lastError

      for (const candidateUri of [requestedUri, `/${requestedUri}`]) {
        try {
          /*
           * Returned as-is, including a `_redirect_` payload. The store must NOT follow the redirect:
           * the catch-all page issues the 301 itself, via Nuxt 3 `navigateTo(to, { redirectCode })`.
           */
          const result = await cmsFetch(`/api/default/${locale.value}/page.json?url=${encodeURI(candidateUri)}`, { ...options })
          if (result) {
            return result
          }
        } catch (error) {
          lastError = error
        }
      }

      // Nothing matched, which is a 404 for the caller rather than a failure.
      if (lastError) {
        throw lastError
      }

      return null
    }

    const result = await cacheStore.fetch({ key, ttl, fetcher })
    if (push) {
      page.value = result
    }
    return result
  }

  async function fetchEducationRedirect({ uri, eventGroupId, eventId, push = false }: { uri: string; eventGroupId: string; eventId: string; push?: boolean }) {
    const key = `education;locale:${locale.value};gid:${eventGroupId};eid:${eventId};uri:${uri}`
    const ttl = 300

    const fetcher = async () => {
      return await cmsFetch(`/api/${locale.value}/education-redirect/${eventGroupId}/event/${eventId}/${encodeURI(uri)}.json`)
    }

    const result = await cacheStore.fetch({ key, ttl, fetcher })
    if (push) {
      page.value = result
    }
    return result
  }

  return { page, languageSwitches, setPage, fetchUri, fetchEducationRedirect }
})
